/**
 * @fileoverview Zcash Wallet Button Component
 * Main wallet connection button with connection status and management
 */

import React, { useState } from 'react';
import { Wallet, ChevronDown, AlertCircle, CheckCircle } from 'lucide-react';
import { useZcashWallet } from '../../hooks/useZcashWallet';
import WalletModal from './WalletModal';

interface ZcashWalletButtonProps {
	className?: string;
	variant?: 'primary' | 'secondary' | 'outline';
	size?: 'sm' | 'md' | 'lg';
	showBalance?: boolean;
	showDropdownMenu?: boolean;
}

const ZcashWalletButton: React.FC<ZcashWalletButtonProps> = ({
	className = '',
	variant = 'primary',
	size = 'md',
	showBalance = true,
	showDropdownMenu = true
}) => {
	const {
		isConnected,
		connection,
		isConnecting,
		connectionError,
		balance,
		isLoadingBalance,
		scanProgress,
		disconnect,
		formatAmount,
		formatAddress
	} = useZcashWallet();

	const [showModal, setShowModal] = useState(false);
	const [showDropdown, setShowDropdown] = useState(false);

	// Button styling based on variant and size
	const getButtonClasses = () => {
		const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';
		
		const variantClasses = {
			primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500',
			secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500',
			outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500'
		};
		
		const sizeClasses = {
			sm: 'px-3 py-1.5 text-sm',
			md: 'px-4 py-2 text-sm',
			lg: 'px-6 py-3 text-base'
		};
		
		const stateClasses = isConnected 
			? 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500'
			: variantClasses[variant];
		
		return `${baseClasses} ${stateClasses} ${sizeClasses[size]} ${className}`;
	};

	// Handle wallet connection
	const handleConnect = () => {
		setShowModal(true);
	};

	// Handle wallet disconnection
	const handleDisconnect = async () => {
		try {
			await disconnect();
			setShowDropdown(false);
		} catch (error) {
			console.error('Disconnect error:', error);
		}
	};

	// Render connection error
	const renderError = () => {
		if (!connectionError) return null;
		
		return (
			<div className="absolute top-full left-0 mt-2 bg-red-50 border border-red-200 rounded-lg p-3 shadow-lg z-50 min-w-64">
				<div className="flex items-start space-x-2">
					<AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
					<div>
						<p className="text-sm font-medium text-red-800">Connection Failed</p>
						<p className="text-xs text-red-600 mt-1">{connectionError}</p>
					</div>
				</div>
			</div>
		);
	};

	// Render wallet dropdown
	const renderDropdown = () => {
		if (!showDropdown || !isConnected || !connection) return null;
		
		return (
			<div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-80">
				<div className="p-4">
					{/* Signing source status */}
					<div className="flex items-center space-x-2 mb-3">
						<CheckCircle className="w-5 h-5 text-green-500" />
						<span className="text-sm font-medium text-gray-900">
							Signing with {
								connection.walletType === 'winbit32' ? 'Winbit32 (in-browser)'
									: connection.walletType === 'cosign' ? 'Winbit32 (co-sign)'
									: connection.walletType
							}
						</span>
					</div>
					
					{/* Address Info */}
					<div className="space-y-2 mb-4">
						<div>
							<p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
								Shielded Address
							</p>
							<p className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
								{formatAddress(connection.address.shielded, 12)}
							</p>
						</div>
						
						{connection.address.transparent && (
							<div>
								<p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
									Transparent Address
								</p>
								<p className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded">
									{formatAddress(connection.address.transparent, 12)}
								</p>
							</div>
						)}
					</div>
					
					{/* Balance Info */}
					{showBalance && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
								Balance
							</p>
							{isLoadingBalance ? (
								<div className="animate-pulse">
									<div className="h-4 bg-gray-200 rounded w-24"></div>
								</div>
							) : balance ? (
								<div className="space-y-1">
									<p className="text-sm text-gray-900">
										Shielded: <span className="font-mono">{formatAmount(balance.shielded)}</span>
									</p>
									{balance.transparent > 0 && (
										<p className="text-sm text-gray-900">
											Transparent: <span className="font-mono">{formatAmount(balance.transparent)}</span>
										</p>
									)}
									<p className="text-sm font-medium text-gray-900 border-t border-gray-200 pt-1">
										Total: <span className="font-mono">{formatAmount(balance.total)}</span>
									</p>
								</div>
							) : (
								<p className="text-sm text-gray-500">Unable to load balance</p>
							)}
						</div>
					)}

					{connection.walletType === 'winbit32' && scanProgress && (
						<div className="mb-4">
							<p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
								Shielded Scan
							</p>
							<div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
								<div
									className="h-full bg-purple-600 transition-all duration-300"
									style={{ width: `${Math.max(0, Math.min(100, scanProgress.percent))}%` }}
								/>
							</div>
							<p className="text-xs text-gray-600 mt-2">
								{scanProgress.status}
							</p>
						</div>
					)}
					
					{/* Actions — label reflects what disconnect actually clears
					    for the connected signer type. */}
					<div className="border-t border-gray-200 pt-3">
						<button
							onClick={handleDisconnect}
							className="w-full text-left px-2 py-1 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
						>
							{connection.walletType === 'cosign'
								? 'Disconnect (forgets the loaded vault share)'
								: connection.walletType === 'winbit32'
									? 'Disconnect (forgets the seed in this browser)'
									: 'Disconnect'}
						</button>
					</div>
				</div>
			</div>
		);
	};

	// Render button content
	const renderButtonContent = () => {
		if (isConnecting) {
			return (
				<>
					<div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
					<span>Connecting...</span>
				</>
			);
		}
		
		if (isConnected && connection) {
			return (
				<>
					<CheckCircle className="w-4 h-4 mr-2" />
					<span className="font-mono">
						{formatAddress(connection.address.shielded, 6)}
					</span>
					{showBalance && balance && (
						<span className="ml-2 px-2 py-0.5 bg-white bg-opacity-20 rounded text-xs">
							{formatAmount(balance.total)}
						</span>
					)}
					{showDropdownMenu && (
						<ChevronDown className="w-4 h-4 ml-2" />
					)}
				</>
			);
		}
		
		return (
			<>
				<Wallet className="w-4 h-4 mr-2" />
				<span>Connect account</span>
			</>
		);
	};

	return (
		<div className="relative">
			<button
				type="button"
				onClick={isConnected ? () => setShowDropdown(!showDropdown) : handleConnect}
				disabled={isConnecting}
				className={getButtonClasses()}
			>
				{renderButtonContent()}
			</button>
			
			{/* Error Display */}
			{renderError()}
			
			{/* Wallet Dropdown */}
			{renderDropdown()}
			
			{/* Connection Modal */}
			<WalletModal
				isOpen={showModal}
				onClose={() => setShowModal(false)}
			/>
			
			{/* Dropdown Backdrop */}
			{showDropdown && (
				<div
					className="fixed inset-0 z-40"
					onClick={() => setShowDropdown(false)}
				/>
			)}
		</div>
	);
};

export default ZcashWalletButton; 